/**
 * @file can.ts
 * @description 角色能力矩陣（Role Capability Matrix）。
 *
 * 對應 spec §2.4 角色可見性矩陣。
 *
 * 使用方式：
 * - `userCan(user, capability)` 優先用 DB 動態能力集，回落 DEFAULT_ROLE_CAPABILITIES
 * - `DEFAULT_ROLE_CAPABILITIES` 字典為內置角色預設矩陣（動態 RBAC 的種子/回落數據源）
 *
 * 注意：行級過濾（如稽核員只看自己案件）由 query 層保證，不靠此矩陣。
 *
 * 能力鍵格式：`<resource>.<action>`
 */
/**
 * 內置角色預設能力矩陣。鍵採取 `<resource>.<action>` 形式。
 * 對應 spec §2.4 角色可見性矩陣。
 * 注意：行級過濾（如稽核員只看自己案件）由 query 層保證，不靠此矩陣。
 *
 * 動態 RBAC 啟用後，此矩陣作為「內置角色種子」與「無 DB 能力集時的回落」，
 * 不再作為運行時最終判斷依據（最終判斷改由 `userCan` 負責）。
 */
export const DEFAULT_ROLE_CAPABILITIES = {
    // ─── P0 系統管理 ─────────────────────────────────────────────────────────────
    /** 用戶帳號管理（建立/修改/停用） */
    'user.manage': ['admin'],
    /** 客戶資料管理（新增/編輯/刪除） */
    'client.manage': ['admin', 'manager'],
    /** 客戶名錄讀取（對外名錄頁、選擇器等只讀場景，所有登入角色可看） */
    'client.read': ['admin', 'manager', 'auditor', 'decider'],
    /** 表單管理 */
    'form-template.manage': ['admin', 'manager'],
    /** 表單上傳識別 */
    'form-template.recognize': ['admin', 'manager'],
    /** 稽核日誌讀取 */
    'audit-log.read': ['admin', 'manager'],
    /** 稽核日誌寫入（通常由系統自動產生） */
    'audit-log.write': ['admin'],
    /** 稽核日誌刪除 */
    'audit-log.delete': ['admin'],
    /** 回收桶查閱 */
    'recycle-bin.read': ['admin', 'manager'],
    /** 回收桶還原 */
    'recycle-bin.restore': ['admin', 'manager'],
    /** 回收桶永久刪除 */
    'recycle-bin.purge': ['admin'],
    /** 系統設定（參數配置等） */
    'system-settings.write': ['admin'],
    /** 系統設定字典讀取（業務字典/客戶字典等下拉選項，所有登入角色都需要） */
    'system-settings.read': ['admin', 'manager', 'auditor', 'decider'],
    /** 編號規則設定 */
    'numbering-rule.write': ['admin'],
    /** 驗證標準管理 */
    'standards.manage': ['admin', 'manager'],
    /** 材料資料管理 */
    'materials.manage': ['admin', 'manager'],
    /** 材料資料讀取（資材選擇器等只讀場景，稽核員做有機資材審查時需要） */
    'materials.read': ['admin', 'manager', 'auditor', 'decider'],
    /** 利益衝突管理 */
    'conflict-of-interest.manage': ['admin', 'manager'],
    /** 通知範本管理 */
    'notification-template.manage': ['admin', 'manager'],
    /** 公告發布 */
    'bulletin.publish': ['admin', 'manager'],
    /** 公佈欄後台管理（含未發布/封存的完整 CRUD） */
    'bulletin.admin': ['admin', 'manager'],
    // ─── M4 統計分析 ─────────────────────────────────────────────────────────────
    /** 統計分析讀取（總覽/案件/稽核員/證書/客戶/財務） */
    'analytics.read': ['admin', 'manager', 'decider', 'verification_lead'],
    /** 統計資料匯出（Excel） */
    'analytics.export': ['admin', 'manager', 'verification_lead'],
    /** 年度市售抽樣查驗統計由驗證組組長簽核。 */
    'market-sampling.sign': ['admin', 'verification_lead'],
    // ─── M1+ 獲證組織名錄 ────────────────────────────────────────────────────────
    /** 獲證組織名錄讀取 */
    'directory.read': ['admin', 'manager', 'auditor', 'decider'],
    /** 獲證組織名錄寫入（新建/編輯/軟刪） */
    'directory.write': ['admin', 'manager'],
    /** 獲證組織名錄 Excel 匯出 */
    'directory.export': ['admin', 'manager'],
    // ─── M1+ 業務功能（P0 預聲明，確保路由存根可通過 typecheck） ──────────────────
    /** 案件派遣 */
    'case.dispatch': ['admin', 'manager'],
    /**
     * 刪除任何案件（軟刪除，可於回收站還原）。
     * 2026-09-15 回報序23：原本與 case.dispatch 綁在一起無法單獨設定，拆出獨立能力，
     * 可於「系統設置 → 角色權限管理」針對角色勾選。未具此能力者仍可刪除自己建立的案件。
     */
    'case.delete': ['admin', 'manager'],
    /** 讀取自己的案件（稽核員視角） */
    'case.read.own': ['auditor'],
    /**
     * 讀取所有案件。
     * 驗證組長需查看全部稽核案件與合約評審，僅授予讀取能力；
     * 派案、案件修改與發證仍由各自獨立 capability 控制。
     */
    'case.read.all': ['admin', 'manager', 'decider', 'verification_lead'],
    /** 驗證決定委員會投票 */
    'decision.vote': ['decider'],
    /**
     * 驗證決定審議安排（技術委員會主委職能：指派 2 位審議成員 + 選決議方式）。
     * 依需求主委身份可為「稽核員或管理組（主委代表）」，故 auditor 亦可安排；
     * 裁審分離由成員指派/簽署的 COI 檢查保證（本案稽核者不得為審議成員）。
     */
    'decision.arrange': ['admin', 'manager', 'auditor'],
    /** 證書發行 */
    'certificate.issue': ['admin', 'manager'],
    /** 技術委員會管理 */
    'tc.manage': ['admin', 'manager'],
    // ─── M2 稽核執行 ─────────────────────────────────────────────────────────────
    /** 稽核任務確認（稽核員確認計畫月份） */
    'audit-task.confirm': ['auditor'],
    /** 稽核任務現場打卡（check-in/check-out） */
    'audit-task.checkin': ['auditor'],
    /** 提交稽核記錄 */
    'audit-record.create': ['auditor'],
    /** 開立 NC/發現事項 */
    'audit-finding.create': ['auditor'],
    /** 採樣登記 */
    'sample.create': ['auditor'],
    /** 上傳實驗室報告 */
    'lab-report.upload': ['auditor', 'admin', 'manager'],
    /** 有機資材審查 */
    'organic-material-review.create': ['auditor'],
    /** 有機資材審查申請（序77–82 獨立模組）：受理登記/受理情形/審查小組 */
    'organic-material-application.manage': ['admin', 'manager', 'auditor'],
    /** 風險評估 */
    'risk-assessment.create': ['auditor'],
    /** AI 報告草稿 */
    'audit-report.draft': ['admin', 'manager', 'auditor'],
    /** 報告樣式模板管理（僅自己的） */
    'report-style.manage': ['auditor'],
    /** 讀取稽核報告 */
    'audit-report.read': ['admin', 'manager', 'decider', 'auditor'],
    /** 提交稽核報告版本進入評審（稽核員） */
    'audit-report.submit': ['auditor'],
    /** 核准 / 退回稽核報告（管理員、經理） */
    'audit-report.review': ['admin', 'manager'],
    /** 稽核通知書管理 */
    'audit-notice.manage': ['admin', 'manager'],
    // ─── M1 申請文件審核 gate ─────────────────────────────────────────────────────
    /** 提交應附文件點檢（序57 新版：提交即生效；開放稽核員作業） */
    'case-document-review.submit': ['admin', 'manager', 'auditor'],
    /** 決議申請文件審核（受理階段早期 gate，由受理側 admin/manager 決議，不依賴稽核組長） */
    'case-document-review.decide': ['admin', 'manager'],
    /**
     * 讀取案件申請文件審核流水。
     * 序47（2026-09-25）：驗證組長進入案件詳情作合約評審時需一併查看應附文件，故納入。
     */
    'case-document-review.read': ['admin', 'manager', 'auditor', 'decider', 'verification_lead'],
    // ─── M1 合約評審 gate（ZHSR01-12） ─────────────────────────────────────────
    /** 建立 / 編輯合約評審草稿、管理組初審（受理階段管理組職能） */
    'case.contract_review.create': ['admin', 'manager'],
    /**
     * 驗證組長提交合約評審（填檢查清單 + 驗證組長意見 + 簽名，序59）。
     * = admin / manager / verification_lead：驗證組長本人與代理人（賦 verification_lead 角色）
     * 皆可登入簽名；admin/manager 保留以相容既有作業流與整合測試。
     */
    'case.contract_review.lead_submit': ['admin', 'manager', 'verification_lead'],
    /** 總經理批示合約評審（受理 / 不受理） */
    'case.contract_review.approve': ['admin'],
    // ─── M1 報價／繳費 admin 糾錯 ────────────────────────────────────────────────
    /** 編輯已建立的報價單／繳款記錄字段（纯糾錯，不影響案件狀態，僅 admin） */
    'case.billing.edit': ['admin'],
    // ─── 序52 報價／繳費管理（會計職能） ─────────────────────────────────────────
    /**
     * 報價管理（建立報價單 / 完結報價）。
     * = admin / manager / accountant：會計（序52）只負責報價與繳費，
     * 故將報價相關端點從 case.dispatch（僅 admin/manager）拆出獨立能力並放行會計；
     * admin/manager 仍具備，行為不變。
     */
    'case.quotation.manage': ['admin', 'manager', 'accountant'],
    /**
     * 繳費管理（登記繳款 / 確認繳費）。
     * = admin / manager / accountant：同上，會計可管理繳費；
     * 派案/建案/刪案等非報價繳費端點仍走 case.dispatch，不受影響。
     */
    'case.payment.manage': ['admin', 'manager', 'accountant'],
    // ─── 動態 RBAC 管理 ──────────────────────────────────────────────────────────
    /** 角色權限管理（動態 RBAC 配置界面與端點，僅 admin） */
    'role.manage': ['admin'],
};
/**
 * 全部能力鍵清單（唯讀陣列）。
 * 供配置界面渲染勾選樹、完整性校驗等使用。
 */
export const ALL_CAPABILITIES = Object.keys(DEFAULT_ROLE_CAPABILITIES);
/**
 * 能力分組定義（配置界面勾選樹的數據源）。
 * 分組鍵與標題對應 can.ts 現有分段結構；每個能力必須恰好出現在一個分組。
 *
 * ⚠️ 完整性約束：CAPABILITY_GROUPS 展開後必須與 ALL_CAPABILITIES 完全一致，
 * can.test.ts 的「能力目錄完整性」測試自動驗證此約束。
 */
export const CAPABILITY_GROUPS = [
    {
        key: 'system',
        title: '系統管理',
        capabilities: [
            'user.manage',
            'role.manage',
            'client.manage',
            'client.read',
            'form-template.manage',
            'form-template.recognize',
            'audit-log.read',
            'audit-log.write',
            'audit-log.delete',
            'recycle-bin.read',
            'recycle-bin.restore',
            'recycle-bin.purge',
            'system-settings.write',
            'system-settings.read',
            'numbering-rule.write',
            'standards.manage',
            'materials.manage',
            'materials.read',
            'conflict-of-interest.manage',
            'notification-template.manage',
            'bulletin.publish',
            'bulletin.admin',
        ],
    },
    {
        key: 'analytics',
        title: '統計分析',
        capabilities: ['analytics.read', 'analytics.export', 'market-sampling.sign'],
    },
    {
        key: 'directory',
        title: '獲證組織名錄',
        capabilities: ['directory.read', 'directory.write', 'directory.export'],
    },
    {
        key: 'case',
        title: '案件與派遣',
        capabilities: [
            'case.dispatch',
            'case.delete',
            'case.read.own',
            'case.read.all',
            'certificate.issue',
            'tc.manage',
        ],
    },
    {
        key: 'decision',
        title: '驗證決定',
        capabilities: ['decision.vote', 'decision.arrange'],
    },
    {
        key: 'audit',
        title: '稽核執行',
        capabilities: [
            'audit-task.confirm',
            'audit-task.checkin',
            'audit-record.create',
            'audit-finding.create',
            'sample.create',
            'lab-report.upload',
            'organic-material-review.create',
            'organic-material-application.manage',
            'risk-assessment.create',
            'audit-report.draft',
            'report-style.manage',
            'audit-report.read',
            'audit-report.submit',
            'audit-report.review',
            'audit-notice.manage',
        ],
    },
    {
        key: 'doc-review',
        title: '申請文件審核',
        capabilities: [
            'case-document-review.submit',
            'case-document-review.decide',
            'case-document-review.read',
        ],
    },
    {
        key: 'contract',
        title: '合約評審',
        capabilities: [
            'case.contract_review.create',
            'case.contract_review.lead_submit',
            'case.contract_review.approve',
        ],
    },
    {
        key: 'billing',
        title: '報價繳費',
        capabilities: ['case.billing.edit', 'case.quotation.manage', 'case.payment.manage'],
    },
];
/**
 * 能力鍵 → 中文操作名稱。
 *
 * 用於把技術性的能力碼（如 `audit-task.checkin`）轉成使用者看得懂的操作描述，
 * 主要供權限不足（403 FORBIDDEN）時組裝友善提示文案使用，
 * 避免直接把英文能力碼暴露給終端使用者。
 */
export const CAPABILITY_LABELS = {
    // ─── P0 系統管理 ─────────────────────────────────────────────────────────────
    'user.manage': '管理用戶帳號',
    'client.manage': '管理客戶資料',
    'client.read': '讀取客戶名錄',
    'form-template.manage': '管理表單',
    'form-template.recognize': '上傳識別表單',
    'audit-log.read': '讀取稽核日誌',
    'audit-log.write': '寫入稽核日誌',
    'audit-log.delete': '刪除稽核日誌',
    'recycle-bin.read': '查閱回收桶',
    'recycle-bin.restore': '還原回收桶項目',
    'recycle-bin.purge': '永久刪除回收桶項目',
    'system-settings.write': '修改系統設定',
    'system-settings.read': '讀取系統設定字典',
    'numbering-rule.write': '設定編號規則',
    'standards.manage': '管理驗證標準',
    'materials.manage': '管理材料資料',
    'materials.read': '讀取材料資料',
    'conflict-of-interest.manage': '管理利益衝突',
    'notification-template.manage': '管理通知範本',
    'bulletin.publish': '發布公告',
    'bulletin.admin': '管理公佈欄',
    // ─── M4 統計分析 ─────────────────────────────────────────────────────────────
    'analytics.read': '讀取統計分析',
    'analytics.export': '匯出統計資料',
    'market-sampling.sign': '簽核市售抽樣統計報表',
    // ─── M1+ 獲證組織名錄 ────────────────────────────────────────────────────────
    'directory.read': '讀取獲證組織名錄',
    'directory.write': '編輯獲證組織名錄',
    'directory.export': '匯出獲證組織名錄',
    // ─── M1+ 業務功能 ────────────────────────────────────────────────────────────
    'case.dispatch': '派遣案件',
    'case.delete': '刪除案件',
    'case.read.own': '讀取本人案件',
    'case.read.all': '讀取所有案件',
    'decision.vote': '驗證決定委員會投票',
    'decision.arrange': '安排驗證決定審議',
    'certificate.issue': '發行證書',
    'tc.manage': '管理技術委員會',
    // ─── M2 稽核執行 ─────────────────────────────────────────────────────────────
    'audit-task.confirm': '確認稽核任務',
    'audit-task.checkin': '現場到達/離開打卡',
    'audit-record.create': '提交稽核記錄',
    'audit-finding.create': '開立 NC／發現事項',
    'sample.create': '登記採樣',
    'lab-report.upload': '上傳實驗室報告',
    'organic-material-review.create': '進行有機資材審查',
    'organic-material-application.manage': '有機資材審查申請管理',
    'risk-assessment.create': '進行風險評估',
    'audit-report.draft': '生成 AI 稽核報告草稿（管理組＝驗證助理可代發起與校對）',
    'report-style.manage': '管理報告樣式模板',
    'audit-report.read': '讀取稽核報告',
    'audit-report.submit': '提交稽核報告進入評審',
    'audit-report.review': '核准／退回稽核報告',
    'audit-notice.manage': '管理稽核通知書',
    // ─── M1 申請文件審核 gate ─────────────────────────────────────────────────────
    'case-document-review.submit': '提交申請文件審核',
    'case-document-review.decide': '決議申請文件審核',
    'case-document-review.read': '讀取申請文件審核流水',
    // ─── M1 合約評審 gate ────────────────────────────────────────────────────────
    'case.contract_review.create': '建立／編輯合約評審',
    'case.contract_review.lead_submit': '驗證組長提交合約評審',
    'case.contract_review.approve': '批示合約評審',
    'case.billing.edit': '編輯報價／繳費',
    'case.quotation.manage': '管理報價（建立／完結）',
    'case.payment.manage': '管理繳費（登記／確認）',
    // ─── 動態 RBAC 管理 ──────────────────────────────────────────────────────────
    'role.manage': '管理角色權限',
};
// ─── 動態 RBAC 函數（Task 1）──────────────────────────────────────────────────
/**
 * 取得某內置角色的預設能力列表。
 * 非內置角色回空陣列（自定義角色的能力由 DB role_capabilities 表決定）。
 *
 * @param role 角色代碼（任意字串；若不在 USER_ROLES 內則回空陣列）
 * @returns    該內置角色在 DEFAULT_ROLE_CAPABILITIES 中擁有的所有能力鍵
 */
export function defaultCapabilitiesForRole(role) {
    return ALL_CAPABILITIES.filter((cap) => DEFAULT_ROLE_CAPABILITIES[cap].includes(role));
}
/**
 * 判斷 user 是否具備某項能力（動態 RBAC 版本）。
 *
 * 優先級：
 * 1. `user.capabilities`（DB 動態能力集）不為 undefined → 以此為準（空陣列 = 無任何能力）。
 * 2. 無 `capabilities` → 按 `user.roles` 走 DEFAULT_ROLE_CAPABILITIES 回落，
 *    相容舊有呼叫點與測試，窗口期零改動。
 *
 * 自定義角色（不在 USER_ROLES 的角色代碼）在回落路徑中不授予任何能力；
 * 其能力應由 DB 動態能力集提供（即呼叫端傳入 `capabilities`）。
 *
 * @param user user 對象（含可選的 capabilities 動態能力集 / roles 角色列表）
 * @param cap  要檢查的能力鍵
 * @returns    true 表示具備該能力
 */
export function userCan(user, cap) {
    // 有動態能力集時直接查集合，忽略 roles（動態 RBAC 路徑）
    if (user.capabilities !== undefined)
        return user.capabilities.includes(cap);
    // 回落：按內置角色預設矩陣判斷（兼容舊行為）
    const allowed = DEFAULT_ROLE_CAPABILITIES[cap];
    return (user.roles ?? []).some((r) => allowed.includes(r));
}
/**
 * 判斷 user 是否為「純驗證決定組」視角（動態 RBAC 版本）。
 *
 * 優先級：
 * 1. `user.workbenchTypes`（DB 動態工作台類型集）不為空 → 以此為準：
 *    含 admin 或 manager 型 → false（管理職權看全部）；
 *    含 decider 型 → true；其他 → false。
 * 2. 無 `workbenchTypes` → 按 `user.roles` 走原 isDeciderScoped 邏輯回落。
 *
 * @param user user 對象（含可選的 workbenchTypes / roles）
 * @returns    true 表示屬純驗證決定組（需行級收斂）
 */
export function userIsDeciderScoped(user) {
    const types = user.workbenchTypes;
    if (types?.length) {
        // 含管理型工作台 → 不收斂
        if (types.includes('admin') || types.includes('manager'))
            return false;
        // 純驗證決定組工作台 → 收斂
        return types.includes('decider');
    }
    // 回落：按 roles 原邏輯判斷（兼容舊行為）
    const owned = user.roles ?? [];
    if (owned.includes('admin') || owned.includes('manager'))
        return false;
    return owned.includes('decider');
}
